//
//  I18n.js
//  Tulip Web Modules SDK
//
//  Internationalization Utility
//  © 2017 Tulip.
//


// - External Dependencies

/// Uses the XML HTTP Request Internationalization Extensions plugin.
/// The export from this plugin is not invoked directly
/// but it is passed to the base framework for configuration.
/// https://github.com/i18next/i18next-xhr-backend#introduction
import XHRBackend from 'i18next-xhr-backend';

// - Internal Dependencies

/// Uses the Browser-Based Language-Detector Internationalization Extensions plugin.
/// The export from this plugin is not invoked directly
/// but it is passed to the base framework for configuration.
/// https://github.com/i18next/i18next-browser-languageDetector#introduction
import LanguageDetector from './detector';

/// Represents the DOM within the context of Internationalization.
import Dom from './Dom';


// - Class Definition

///  Internationalization Utility
export default class I18n {

  // - Instance Fields

  /// Sub-utility for HTML internationalization.
  Dom;

  /// The instance of the internationalization framework to which all actual work is delegated.
  i18nFramework;

  /// A symbol which represents this translation subsystem for Verifiable Localization.
  /// undefined when Verifiable Localization is not permitted or is not enabled.
  verifiableL10n;

  // - Instance Properties

  /// The language currently being used as translation destination,
  /// whether detected or manually set.
  get language() {
    return this.i18nFramework.language;
  }

  // - Instance Lifecycle

  /// Constructs a `new` I18n instance.
  /// Note:  Do not invoke this constructor directly; use either the default exported instance or
  ///        the `createInstance()` export from this directory's index.js file,
  ///        as these handle creating and passing the appropriate arguments for you.
  /// Paramters:
  /// + i18next:  A constructed but uninitialized instance of the internationalization framework
  ///             which underpins the workings of this class.
  /// + dom:  A constructed and (optionally) initialized instance of a DOM representation.
  constructor(i18next, dom) {
    this.i18nFramework = i18next;
    this.Dom = new Dom(i18next, dom);
    // tulipNativeBridge is actually defined globally on the iPad, but ESLint doesn't know that.
    // We check for it being defined so that this SDK doesn't fail
    // on HQ where tulipNativeBridige is NOT defined.
    // eslint-disable-next-line no-undef
    this.verifiableL10n = {};
    // eslint-disable-next-line no-undef
    if (typeof tulipNativeBridge !== 'undefined') {
      // eslint-disable-next-line no-undef
      this.verifiableL10n = (tulipNativeBridge.context.verifiable_l10n || {}).js;
    } else {
      this.verifiableL10n = ({}).js;
    }

    // Load plugins into I18nExt.
    this.i18nFramework
      // Uses XHR to load the strings JSON.
      .use(XHRBackend)
      // Uses the configured order to detect language,
      // including via the Tulip Web-to-Native Bridge if available.
      .use(LanguageDetector);

    this.i18nFramework.on('initialized', async () => {
      await this.Dom.init();
    });
  }

  // - Instance Methods

  /// Initializes the receiver with the given configuration options.
  /// https://www.i18next.com/overview/configuration-options
  init(overrideOptions) {
    this.i18nFramework.init({
      // https://www.i18next.com/overview/configuration-options
      // Tulip 'default' configuration which takes precedence over the plugin defaults.
      debug: false,
      nsSeparator: false,
      keySeparator: false,
      fallbackLng: 'en',
      defaultNS: 'common',
      fallbackNS: ['common', 'terms'],
      interpolation: {},
      backend: {
        // Configuration for XHR Backend plugin.
        // https://github.com/i18next/i18next-xhr-backend#backend-options
        allowMultiLoading: true,
        loadPath: '/sat-module-framework/locales/?lng={{lng}}&ns={{ns}}',
      },
      detection: {
        // Configuration for Language Detection plugin.
        // https://github.com/i18next/i18next-browser-languageDetector#detector-options
        order: ['querystring', 'navigator', 'nativeBridge', 'htmlTag'],
        lookupQuerystring: 'language',
      },
      // Module / SDK override configuration which takes precednce over all Tulip & plugin defaults.
      ...overrideOptions,
      // Except: With all other configuration, override is a replace operation;
      //         but for the namespace configuration, the override configuration
      //         is *merged* with the Tulip 'default' for this configuration.
      ns: ['common', 'terms'].concat(overrideOptions.ns || []),
    });

    return new Promise(resolve => this.i18nFramework.on('initialized', () => resolve(this)));
  }

  /// Translates the given text.
  /// Parameters:
  /// + key(s):   A key or array of keys to translate.
  /// + options:  A default string or an object containing interpolation arguments,
  ///             or other context (optional).
  /// https://www.i18next.com/translation-function/essentials
  /// https://www.i18next.com/translation-function/interpolation
  /// https://www.i18next.com/translation-function/plurals
  translate(...args) {
    const result = this.i18nFramework.t(...args);
    return typeof this.verifiableL10n === 'string' ? this.verifiableL10n + result : result;
  }

}



// WEBPACK FOOTER //
// ./src/i18n/I18n.js